import React, { useEffect, useState, useCallback, useRef } from 'react'
import { extractNumericId } from '../lib/userIdExtractor'
import { API_BASE_URL } from '../constants'
import { getSessionHeaders } from '../lib/serverSession'
import { effectiveRowShares, isBonusOnlyRow, isReversalRow, isRecreditRow, isActiveBonusRow, displayPackageLabel } from '../lib/shareRowDisplay'

// ── Types ──────────────────────────────────────────────────────────────
interface Purchase {
  id: string; shares_purchased: number; bonus_shares: number; total_amount: number;
  price_per_share: number; payment_method: string; package_name: string;
  transaction_reference?: string | null; created_at: string; phase_id: number;
  bonus_breakdown?: { label: string; shares: number }[];
}
interface Commission {
  id: string; share_commission: number; usdt_commission: number; share_purchase_amount: number;
  referred_id: number; created_at: string; phase_id: number;
}
interface Conversion {
  id: string; shares_requested: number; usdt_amount: number; share_price: number;
  status: string; created_at: string; phase_id: number;
}
interface Topup {
  id: string; topup_payment_amount: number | null; total_amount: number | null;
  transaction_reference: string | null; transaction_notes: string | null;
  status: string; created_at: string;
}
interface Topup {
  id: string; topup_payment_amount: number | null; total_amount: number | null;
  transaction_reference: string | null; transaction_notes: string | null;
  status: string; created_at: string;
}
interface SourcePurchase {
  id: string; shares_purchased: number; total_amount: number; price_per_share: number;
  package_name: string | null; phase_id: number | null; payment_method: string | null;
  transaction_reference: string | null; purchase_date: string | null; original_owner_user_id: number | null;
}
interface Transfer {
  id: string; shares_transferred: number; sender_user_id?: number; recipient_user_id?: number;
  created_at: string; status: string;
  // Enriched (received transfers only)
  sender_username?: string | null;
  source_transaction_hash?: string | null;
  source_crypto_payment_transaction_id?: string | null;
  transfer_reason?: string | null;
  source_purchase?: SourcePurchase | null;
  // Enriched (sent transfers only)
  source_share_purchase_id?: string | null;
}
interface Summary {
  totalShares: number; purchasedShares: number; bonusShares: number; presaleCommissionShares: number;
  convertedShares: number; legacyShares: number; sharesSent: number; sharesReceived: number; netTransfers: number;
  currentSharePrice: number | null; portfolioValue: number | null; totalInvested: number;
  availableSupply: number; phaseName: string;
  // Commission wallet bucket (from the shared canonical calculator) — never summed
  // with equity shares. walletShares is already part of totalShares (informational).
  walletUsdt?: number; walletShares?: number; residualUsdt?: number; canonicalFlags?: string[];
}
interface ShareData {
  summary: Summary; purchases: Purchase[]; commissions: Commission[];
  conversions: Conversion[]; sentTransfers: Transfer[]; receivedTransfers: Transfer[];
  topups?: Topup[];
}

interface MySharesPageProps { user: any }

// ── Helpers ────────────────────────────────────────────────────────────
const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'
// Display-only: make consolidated-bonus rows read clearly as bonus shares (past & future rows).
// Consolidation writes payment_method='Consolidated Bonus' and package_name='Consolidated bonus from N purchases'.
// This only changes the human-readable label; it never alters any share/bonus amount.
const bonusLabel = (p: Purchase): string => {
  const raw = (p.package_name || p.payment_method || '').toString()
  const isConsolidated = p.payment_method === 'Consolidated Bonus' || /^consolidated bonus from/i.test(raw)
  if (isConsolidated) {
    const m = raw.match(/from\s+(\d+)\s+purchases?/i)
    const n = m ? m[1] : null
    return n ? `Bonus Shares (consolidated from ${n})` : 'Bonus Shares (consolidated)'
  }
  // Display-only: stored "Birthday" package labels render as "Anniversary".
  return displayPackageLabel(raw)
}
const topupAmount = (t: Topup) => parseFloat(String(t.topup_payment_amount ?? t.total_amount ?? 0)) || 0
const topupNetwork = (t: Topup): string | null => {
  if (!t.transaction_notes) return null
  try { const n = JSON.parse(t.transaction_notes); return n?.network || null } catch { return null }
}

// ── Consolidate Button with Professional Modal ──────────────────────
const ConsolidateButton: React.FC<{
  consolidationType: string; label?: string; userId: number;
  onSuccess: () => void; minRecords?: number; recordCount: number;
  totalShares: number;
  // Optional itemised list of the exact shares being consolidated. When supplied
  // (e.g. for the bonus/birthday flow) the modal lists each source so users can
  // clearly see their bonus & birthday shares before consolidating. Display-only.
  breakdownItems?: { label: string; shares: number }[];
}> = ({ consolidationType, label, onSuccess, minRecords = 2, recordCount, totalShares, breakdownItems }) => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [result, setResult] = useState<{ success?: boolean; message?: string; error?: string } | null>(null)

  if (recordCount < minRecords) return null

  const wholeShares = Math.floor(totalShares)
  const fractionalRemainder = Math.round((totalShares - wholeShares) * 10000) / 10000
  const typeLabel = consolidationType.replace(/_/g, ' ')

  const handleConsolidate = async () => {
    setLoading(true); setResult(null)
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/consolidate-shares`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ consolidationType })
      })
      const json = await res.json()
      if (!res.ok || !json.success) { setResult({ error: json.error || 'Consolidation failed' }); setLoading(false); return }
      setResult({ success: true, message: json.message })
      setTimeout(() => { setResult(null); setShowModal(false); onSuccess() }, 2500)
    } catch (e: any) { setResult({ error: e.message || 'Network error' }) }
    finally { setLoading(false) }
  }

  return (
    <>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm text-gray-900 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 hover:from-yellow-300 hover:via-amber-300 hover:to-yellow-400 shadow-md shadow-yellow-900/30 transition-all duration-200"
        >
          <span>🔗</span> {label || 'Consolidate into Whole Shares'}
        </button>
      </div>

      {/* ── Consolidation Confirmation Modal ─── */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-sm p-4" onClick={() => !loading && setShowModal(false)}>
          <div className="min-h-full flex items-center justify-center py-6">
          <div className="bg-gray-800 rounded-2xl border border-yellow-600/40 shadow-2xl shadow-yellow-900/20 max-w-md w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-1">Consolidate {typeLabel}</h3>
            <p className="text-gray-400 text-sm mb-5">Review the consolidation details below before proceeding.</p>

            {/* Breakdown */}
            <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-700/50 space-y-3 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Records to consolidate</span>
                <span className="text-white font-semibold">{recordCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Total shares</span>
                <span className="text-yellow-400 font-semibold">{fmt(totalShares)}</span>
              </div>
              {breakdownItems && breakdownItems.length > 0 && (
                <div className="border-t border-gray-700 pt-3 space-y-1.5">
                  <p className="text-gray-500 text-[11px] uppercase tracking-wider">Bonus &amp; birthday shares included</p>
                  <div className="max-h-40 overflow-y-auto pr-1 space-y-1.5">
                  {breakdownItems.map((item, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm flex items-center gap-1.5"><span>🎁</span>{item.label}</span>
                      <span className="text-yellow-300 font-medium text-sm">+{fmt(item.shares)}</span>
                    </div>
                  ))}
                  </div>
                </div>
              )}
              <div className="border-t border-gray-700 pt-3 flex justify-between items-center">
                <span className="text-gray-400 text-sm">Whole shares you will receive</span>
                <span className="text-green-400 font-bold text-lg">{wholeShares}</span>
              </div>
              {fractionalRemainder > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Fractional remainder</span>
                  <span className="text-orange-400 font-semibold">{fmt(fractionalRemainder)}</span>
                </div>
              )}
            </div>

            {/* Warning */}
            <div className="bg-amber-900/30 border border-amber-600/40 rounded-xl p-4 mb-5">
              <p className="text-amber-300 text-sm font-semibold mb-1">⚠️ Important</p>
              <p className="text-amber-200/80 text-sm leading-relaxed">
                Shares <strong>must be consolidated</strong> to count towards your <strong>share certificate</strong>.
                Unconsolidated fractional records will not be included.
              </p>
            </div>

            {/* Result feedback */}
            {result?.success && <div className="bg-green-900/30 border border-green-600/40 rounded-lg p-3 mb-4 text-green-300 text-sm font-medium">✅ {result.message}</div>}
            {result?.error && <div className="bg-red-900/30 border border-red-600/40 rounded-lg p-3 mb-4 text-red-300 text-sm font-medium">❌ {result.error}</div>}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConsolidate}
                disabled={loading || !!result?.success}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold text-gray-900 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 hover:from-yellow-300 hover:via-amber-300 hover:to-yellow-400 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2"><span className="w-4 h-4 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" /> Consolidating…</span>
                ) : 'Confirm Consolidation'}
              </button>
            </div>
          </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Component ──────────────────────────────────────────────────────────
const ADMIN_USER_ID = 106

interface SearchResult { id: number; username: string; full_name: string | null }

const MySharesPage: React.FC<MySharesPageProps> = ({ user }) => {
  const [data, setData] = useState<ShareData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ whole: true, bonus: true })

  // Admin user lookup (user 106 only)
  const ownUserId = extractNumericId(user?.database_user?.id || user?.id)
  const isAdmin = ownUserId === ADMIN_USER_ID
  const [viewingUserId, setViewingUserId] = useState<number | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const [viewingLabel, setViewingLabel] = useState<string | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // The effective userId for data fetching
  const userId = viewingUserId || ownUserId

  // Search users (debounced)
  useEffect(() => {
    if (!isAdmin || searchInput.trim().length < 1) { setSearchResults([]); setSearching(false); return }
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        console.log('🔍 [MyShares] Searching:', searchInput.trim(), 'as userId:', ownUserId)
        const res = await fetch(`${API_BASE_URL}/api/user/my-shares`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ operation: 'search', searchQuery: searchInput.trim(), requestingUserId: ownUserId })
        })
        const json = await res.json()
        console.log('🔍 [MyShares] Search response:', json)
        if (json.success) { setSearchResults(json.users || []); setSearchOpen(true) }
        else { console.warn('🔍 [MyShares] Search failed:', json.error); setSearchResults([]) }
      } catch (err) { console.error('🔍 [MyShares] Search error:', err) }
      setSearching(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, isAdmin, ownUserId])

  const selectUser = (u: SearchResult) => {
    setViewingUserId(u.id)
    setViewingLabel(`${u.username} (#${u.id})`)
    setSearchInput('')
    setSearchResults([])
    setSearchOpen(false)
  }

  const resetToOwn = () => {
    setViewingUserId(null)
    setViewingLabel(null)
    setSearchInput('')
  }

  const fetchData = useCallback(async () => {
    if (!userId) { setError('Unable to determine user ID'); setLoading(false); return }
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/my-shares`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId })
      })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load')
      setData(json.data)
    } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }, [userId])

  useEffect(() => { fetchData() }, [fetchData])

  const toggle = (key: string) => setOpenSections(p => ({ ...p, [key]: !p[key] }))

  // ── Loading / Error states ─────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">Loading your share portfolio…</p>
      </div>
    </div>
  )
  if (error) return (
    <div className="bg-red-900/30 border border-red-700 rounded-xl p-6 text-center">
      <p className="text-red-400 text-lg mb-3">⚠️ {error}</p>
      <button onClick={fetchData} className="bg-yellow-600 hover:bg-yellow-700 text-white px-5 py-2 rounded-lg">Retry</button>
    </div>
  )
  if (!data) return null

  const { summary: s } = data
  const priceKnown = typeof s.currentSharePrice === 'number' && s.currentSharePrice > 0 && typeof s.portfolioValue === 'number'
  const gain = priceKnown ? s.portfolioValue - s.totalInvested : 0
  const gainPct = priceKnown && s.totalInvested > 0 ? (gain / s.totalInvested) * 100 : 0

  // Split purchases into direct purchases vs commission-level purchases
  const isCommissionPurchase = (p: Purchase) => p.payment_method?.startsWith('COMMISSION_LEVEL')
  const directPurchases = data.purchases.filter(p => !isCommissionPurchase(p) && p.payment_method !== 'Commission Conversion')
  const commissionPurchases = data.purchases.filter(p => isCommissionPurchase(p))

  // Bonus line = live gift rows + consolidated bonus rows (still equity), excluding
  // VOID reversals and RECREDIT restorations (those are paid/audit lines).
  const bonusPurchases = directPurchases.filter(p => isActiveBonusRow(p))
  const paidPurchases = directPurchases.filter(p => !isActiveBonusRow(p))

  // Section totals (presentational). Paid + Bonus must NOT double-count the same row.
  const paidShareTotal = paidPurchases.reduce((a, p) => a + effectiveRowShares(p), 0)
  const bonusSharesTotal = bonusPurchases.reduce((a, p) => a + effectiveRowShares(p), 0)
  const paidAmountTotal = paidPurchases.reduce((a, p) => a + parseFloat(String(p.total_amount || 0)), 0)
  const directBonusTotal = paidPurchases.reduce((a, p) => a + (isBonusOnlyRow(p) ? 0 : parseFloat(String(p.bonus_shares || 0))), 0)
  const commissionPurchaseShareTotal = commissionPurchases.reduce((a, p) => a + parseFloat(String(p.shares_purchased || 0)), 0)
  const commissionShareTotal = data.commissions.reduce((a, c) => a + parseFloat(String(c.share_commission || 0)), 0)
  const __commissionUsdtTotal = data.commissions.reduce((a, c) => a + parseFloat(String(c.usdt_commission || 0)), 0)
  const conversionShareTotal = data.conversions.reduce((a, c) => a + parseFloat(String(c.shares_requested || 0)), 0)
  const conversionUsdtTotal = data.conversions.reduce((a, c) => a + parseFloat(String(c.usdt_amount || 0)), 0)
  const sentTotal = data.sentTransfers.reduce((a, t) => a + parseFloat(String(t.shares_transferred || 0)), 0)
  const receivedTotal = data.receivedTransfers.reduce((a, t) => a + parseFloat(String(t.shares_transferred || 0)), 0)
  // Approved commission top-ups (display-only funding audit trail — no share impact)
  const topups = data.topups || []
  const topupTotal = topups.reduce((a, t) => a + topupAmount(t), 0)

  // Source ASP ids that the user has fully transferred out (from already-loaded sentTransfers).
  // Used to render a non-destructive "Transferred out" indicator on Direct Purchases rows.
  // No extra API call — relies on the transfer_metadata.source_share_purchase_id already
  // enriched server-side in /api/user/my-shares.
  const transferredOutAspIds = new Set(
    data.sentTransfers.map(t => t.source_share_purchase_id).filter((v): v is string => !!v)
  )

  // Must match api/user/consolidate-shares.js consolidateBonusShares: every ACTIVE
  // row with bonus_shares > 0 (including mixed paid+bonus purchases), not only
  // bonus-only gift rows. Modal count/total must equal what the server will move.
  const consolidatableBonus = data.purchases.filter(p => parseFloat(String(p.bonus_shares || 0)) > 0)
  const consolidatableBonusTotal = consolidatableBonus.reduce(
    (a, p) => a + parseFloat(String(p.bonus_shares || 0)),
    0
  )

  // How the headline total is built (display-only equation).
  const breakdownParts: { label: string; value: number; sign?: '+' | '-' }[] = []
  if (paidShareTotal !== 0) breakdownParts.push({ label: 'paid', value: paidShareTotal })
  if (bonusSharesTotal > 0) breakdownParts.push({ label: 'bonus', value: bonusSharesTotal, sign: '+' })
  if (commissionPurchaseShareTotal > 0) breakdownParts.push({ label: 'commission shares', value: commissionPurchaseShareTotal, sign: '+' })
  if (s.convertedShares > 0) breakdownParts.push({ label: 'converted', value: s.convertedShares, sign: '+' })
  if (s.legacyShares > 0) breakdownParts.push({ label: 'legacy', value: s.legacyShares, sign: '+' })
  if (s.sharesReceived > 0) breakdownParts.push({ label: 'received', value: s.sharesReceived, sign: '+' })
  if (s.sharesSent > 0) breakdownParts.push({ label: 'sent', value: s.sharesSent, sign: '-' })
  const breakdownEquation = breakdownParts.length > 0
    ? breakdownParts
        .map((p, i) => {
          const abs = fmt(Math.abs(p.value))
          if (i === 0) return `${fmt(p.value)} ${p.label}`
          const op = p.sign === '-' || p.value < 0 ? '−' : '+'
          return `${op} ${abs} ${p.label}`
        })
        .join(' ')
      + ` = ${fmt(s.totalShares)} owned`
    : null

  return (
    <div className="space-y-6">
      {/* ── Admin User Selector (user 106 only) ──────────────────── */}
      {isAdmin && (
        <div className="bg-gray-800 rounded-xl border border-blue-600/40 p-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-blue-400 text-sm font-semibold">🔍 Admin: View Another User's Shares</span>
            {viewingLabel && (
              <span className="text-yellow-400 text-sm">
                Viewing: <span className="font-bold">{viewingLabel}</span>
                <button onClick={resetToOwn} className="ml-2 text-gray-400 hover:text-red-400 text-sm underline">Reset to mine</button>
              </span>
            )}
          </div>
          <div className="relative" ref={searchRef}>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
              placeholder="Search by username or user ID…"
              className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition"
            />
            {searchOpen && searchResults.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {searchResults.map(u => (
                  <button
                    key={u.id}
                    onClick={() => selectUser(u)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-700 transition-colors flex items-center justify-between border-b border-gray-800 last:border-0"
                  >
                    <span className="text-white text-sm font-medium">{u.username}</span>
                    <span className="text-gray-500 text-sm">#{u.id}{u.full_name ? ` · ${u.full_name}` : ''}</span>
                  </button>
                ))}
              </div>
            )}
            {searchInput.trim().length > 0 && !searching && searchResults.length === 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-500 text-sm">No users found</div>
            )}
            {searching && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm">Searching…</div>
            )}
          </div>
        </div>
      )}

      {/* ── Summary Header ──────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-gray-800 via-gray-850 to-gray-900 rounded-2xl border border-yellow-600/30 p-6 md:p-8 shadow-lg shadow-yellow-900/10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">📊 {viewingLabel ? `${viewingLabel}'s Shares` : 'My Shares Portfolio'}</h2>
          <button onClick={fetchData} className="text-gray-400 hover:text-yellow-400 transition text-lg" title="Refresh">🔄</button>
        </div>
        {/* Big number */}
        <div className="text-center mb-8">
          <p className="text-white text-sm uppercase tracking-[0.2em] mb-2">Equity Shares (Owned)</p>
          <p className="text-5xl md:text-6xl font-extrabold text-yellow-400 tracking-tight">{fmt(s.totalShares)}</p>
          <p className="text-gray-500 mt-2 text-sm">@ <span className="text-gray-300">{priceKnown ? `$${fmt(s.currentSharePrice)}` : '...'}</span> per share&nbsp;&nbsp;·&nbsp;&nbsp;<span className="text-gray-300">{s.phaseName}</span></p>
          {breakdownEquation && (
            <p className="text-gray-400 mt-3 text-sm leading-relaxed max-w-3xl mx-auto" title="How your owned total is built from the lines below">
              {breakdownEquation}
            </p>
          )}
        </div>
        {/* 4 key metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-900/60 rounded-xl p-4 border border-yellow-600/40 text-center">
            <p className="text-white text-[10px] uppercase tracking-wider mb-1">Portfolio Value</p>
            <p className="text-xl font-bold text-yellow-400">{priceKnown ? `$${fmt(s.portfolioValue)}` : '...'}</p>
          </div>
          <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-700/50 text-center">
            <p className="text-white text-[10px] uppercase tracking-wider mb-1">Amount Paid</p>
            <p className="text-xl font-bold text-white">${fmt(s.totalInvested)}</p>
          </div>
          <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-700/50 text-center">
            <p className="text-white text-[10px] uppercase tracking-wider mb-1">Unrealised Gain</p>
            <p className={`text-xl font-bold ${gain >= 0 ? 'text-green-400' : 'text-red-400'}`}>{priceKnown ? `$${fmt(gain)}` : '...'}</p>
            <p className={`text-[10px] ${gain >= 0 ? 'text-green-500' : 'text-red-500'}`}>{priceKnown ? `${gainPct >= 0 ? '+' : ''}${fmt(gainPct)}%` : '...'}</p>
          </div>
          <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-700/50 text-center">
            <p className="text-white text-[10px] uppercase tracking-wider mb-1">Available Supply</p>
            <p className="text-xl font-bold text-white">{(s.availableSupply ?? 0).toLocaleString()}</p>
          </div>
        </div>
        {/* Share Source Breakdown — Paid and Bonus are separate so free/BOGO shares stay visible after consolidation */}
        <div className="bg-gray-900/40 rounded-xl p-4 border border-gray-700/30">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-3 text-center">Share Breakdown</p>
          <div className="flex flex-wrap justify-center gap-2">
            {paidShareTotal !== 0 && <BreakdownPill label="Paid" value={fmt(paidShareTotal)} />}
            {bonusSharesTotal > 0 && <BreakdownPill label="Bonus" value={`+${fmt(bonusSharesTotal)}`} color="text-yellow-400" />}
            {commissionPurchaseShareTotal > 0 && <BreakdownPill label="Commissions" value={fmt(commissionPurchaseShareTotal)} />}
            {s.convertedShares > 0 && <BreakdownPill label="Converted" value={fmt(s.convertedShares)} />}
            {s.legacyShares > 0 && <BreakdownPill label="Legacy" value={fmt(s.legacyShares)} />}
            {s.sharesReceived > 0 && <BreakdownPill label="Received" value={`+${fmt(s.sharesReceived)}`} color="text-green-400" />}
            {s.sharesSent > 0 && <BreakdownPill label="Sent" value={`-${fmt(s.sharesSent)}`} color="text-red-400" />}
          </div>
          {breakdownEquation && (
            <p className="mt-3 text-center text-gray-500 text-xs leading-relaxed">
              Each line above is part of your owned total. Free BOGO / gift / consolidated bonus shares appear on the Bonus line so they stay visible even after consolidation.
            </p>
          )}
        </div>
        {/* Commission Wallet — a SEPARATE bucket from owned shares, never summed with equity. */}
        <div className="bg-gray-900/40 rounded-xl p-4 border border-gray-700/30 mt-4">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-3 text-center">Commission Wallet — separate from owned shares</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-white text-[10px] uppercase tracking-wider mb-1">Spendable USDT</p>
              <p className="text-xl font-bold text-yellow-400">${fmt(s.walletUsdt ?? 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-white text-[10px] uppercase tracking-wider mb-1">Commission Shares Earned</p>
              <p className="text-xl font-bold text-white">{fmt(s.walletShares ?? 0)}</p>
            </div>
          </div>
          <p className="mt-3 text-gray-500 text-xs text-center leading-relaxed">
            USDT is your withdrawable commission balance. Commission shares earned are already counted in your equity shares above — shown here for reference and never added twice.
          </p>
        </div>
      </div>

      {/* ── Dividend & Certificate Warning ────────────────────────── */}
      {(commissionPurchases.length > 0 || data.commissions.length > 0 || data.conversions.length > 0 || bonusPurchases.length > 0 || s.legacyShares > 0 || data.receivedTransfers.length > 0) && (
        <div className="bg-amber-900/20 border border-amber-600/30 rounded-xl p-4 flex items-start gap-3">
          <span className="text-amber-400 text-xl mt-0.5">⚠️</span>
          <div>
            <p className="text-amber-300 font-semibold text-sm mb-1">Consolidation Required for Dividends & Certificates</p>
            <p className="text-amber-200/70 text-sm leading-relaxed">
              Fractional shares, commissions, conversions, bonus shares, legacy bonus, and received transfers must be <strong>consolidated into whole shares</strong> before they count towards your <strong>share certificate</strong>. Use the consolidation buttons below to merge your records.
            </p>
          </div>
        </div>
      )}

      {/* ── Transaction Sections ────────────────────────────────────── */}
      {paidPurchases.length > 0 && <AccordionSection title="💰 Paid Shares" count={paidPurchases.length} total={`${fmt(paidShareTotal)} shares · $${fmt(paidAmountTotal)}`} isOpen={!!openSections.whole} onToggle={() => toggle('whole')}>
        <PaginatedTable
          items={paidPurchases}
          head={<tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
            <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Shares</th><th className="text-right font-medium">Bonus</th><th className="text-right font-medium">Price</th><th className="text-right font-medium">Amount</th><th className="text-left pl-4 font-medium">Method</th>
          </tr>}
          renderRow={p => {
            const isTransferredOut = transferredOutAspIds.has(p.id)
            return (
              <tr key={p.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
                <td className="py-2.5 text-gray-300 text-sm">{fmtDate(p.created_at)}</td>
                <td className="text-right text-white font-medium">
                  {isRecreditRow(p) && (
                    <span className="mr-1" title="Restored shares (correction)">↩</span>
                  )}
                  <span className={isReversalRow(p) ? 'text-red-400' : ''}>{fmt(effectiveRowShares(p))}</span>
                  {isReversalRow(p) && (
                    <span
                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-900/40 text-red-300 border border-red-700/40"
                      title="Auditable reversal / void of a previously issued gift grant. This row subtracts shares from your total."
                    >
                      ↩ REVERSED
                    </span>
                  )}
                  {isRecreditRow(p) && (
                    <span
                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-900/40 text-emerald-300 border border-emerald-700/40"
                      title="Shares restored by an audited correction. Counts toward your paid total, not the bonus line."
                    >
                      RESTORED
                    </span>
                  )}
                  {isTransferredOut && (
                    <span
                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-900/40 text-red-300 border border-red-700/40"
                      title="These shares were transferred to another account. Net ownership of this purchase is no longer with you."
                    >
                      TRANSFERRED OUT
                    </span>
                  )}
                </td>
                <td className="text-right text-yellow-400 text-sm">{!isBonusOnlyRow(p) && (p.bonus_shares ?? 0) > 0 ? `+${fmt(p.bonus_shares)}` : '—'}</td>
                <td className="text-right text-white">${fmt(p.price_per_share)}</td>
                <td className="text-right text-green-400 font-medium">${fmt(p.total_amount)}</td>
                <td className="pl-4 text-gray-500 text-sm">{p.payment_method}</td>
              </tr>
            )
          }}
          footer={<tr className="border-t-2 border-yellow-600/30 bg-gray-900/40">
            <td className="py-3 text-yellow-400 font-semibold text-sm">TOTAL</td>
            <td className="text-right text-yellow-400 font-bold">{fmt(paidShareTotal)}</td>
            <td className="text-right text-yellow-400 font-bold text-sm">{directBonusTotal > 0 ? `+${fmt(directBonusTotal)}` : '—'}</td>
            <td className="text-right text-gray-500">—</td>
            <td className="text-right text-yellow-400 font-bold">${fmt(paidAmountTotal)}</td>
            <td></td>
          </tr>}
        />
      </AccordionSection>}

      {commissionPurchases.length > 0 && <AccordionSection title="🔢 Commission Shares" count={commissionPurchases.length} total={`${fmt(commissionPurchaseShareTotal)} shares`} isOpen={!!openSections.fractional} onToggle={() => toggle('fractional')}>
        <PaginatedTable
          items={commissionPurchases}
          head={<tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
            <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Shares</th><th className="text-right font-medium">Price</th><th className="text-right font-medium">Amount</th><th className="text-left pl-4 font-medium">Level</th>
          </tr>}
          renderRow={p => (
            <tr key={p.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
              <td className="py-2.5 text-gray-300 text-sm">{fmtDate(p.created_at)}</td>
              <td className="text-right text-white font-medium">{fmt(p.shares_purchased)}</td>
              <td className="text-right text-white">${fmt(p.price_per_share)}</td>
              <td className="text-right text-green-400 font-medium">${fmt(p.total_amount)}</td>
              <td className="pl-4 text-gray-500 text-sm">{p.payment_method?.replace('COMMISSION_LEVEL_', 'Level ')}</td>
            </tr>
          )}
          footer={<tr className="border-t-2 border-yellow-600/30 bg-gray-900/40">
            <td className="py-3 text-yellow-400 font-semibold text-sm">TOTAL</td>
            <td className="text-right text-yellow-400 font-bold">{fmt(commissionPurchaseShareTotal)}</td>
            <td className="text-right text-gray-500">—</td>
            <td></td>
            <td></td>
          </tr>}
        />
        <ConsolidateButton consolidationType="fractional_purchases" userId={userId as number} onSuccess={fetchData} recordCount={commissionPurchases.length} totalShares={commissionPurchaseShareTotal} />
      </AccordionSection>}

      {data.commissions.length > 0 && <AccordionSection title="🤝 Referral Commissions" count={data.commissions.length} total={`${fmt(commissionShareTotal)} shares`} isOpen={!!openSections.commissions} onToggle={() => toggle('commissions')}>
        {data.commissions.length === 0 ? <Empty /> : (
          <PaginatedTable
            items={data.commissions}
            head={<tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
              <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Share Commission</th><th className="text-right font-medium">From Purchase</th>
            </tr>}
            renderRow={c => (
              <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
                <td className="py-2.5 text-gray-300 text-sm">{fmtDate(c.created_at)}</td>
                <td className="text-right text-yellow-400 font-medium">{fmt(c.share_commission)}</td>
                <td className="text-right text-white">${fmt(c.share_purchase_amount)}</td>
              </tr>
            )}
            footer={<tr className="border-t-2 border-yellow-600/30 bg-gray-900/40">
              <td className="py-3 text-yellow-400 font-semibold text-sm">TOTAL</td>
              <td className="text-right text-yellow-400 font-bold">{fmt(commissionShareTotal)}</td>
              <td></td>
            </tr>}
          />
        )}
        <ConsolidateButton consolidationType="commissions" userId={userId as number} onSuccess={fetchData} recordCount={data.commissions.length} totalShares={commissionShareTotal} />
      </AccordionSection>}

      {data.conversions.length > 0 && <AccordionSection title="🔄 USDT Conversions" count={data.conversions.length} total={`${fmt(conversionShareTotal)} shares · $${fmt(conversionUsdtTotal)} USDT`} isOpen={!!openSections.conversions} onToggle={() => toggle('conversions')}>
        {data.conversions.length === 0 ? <Empty /> : (
          <PaginatedTable
            items={data.conversions}
            head={<tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
              <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Shares</th><th className="text-right font-medium">USDT Used</th><th className="text-right font-medium">Price/Share</th>
            </tr>}
            renderRow={c => (
              <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
                <td className="py-2.5 text-gray-300 text-sm">{fmtDate(c.created_at)}</td>
                <td className="text-right text-yellow-400 font-medium">{fmt(c.shares_requested)}</td>
                <td className="text-right text-green-400">${fmt(c.usdt_amount)}</td>
                <td className="text-right text-white">${fmt(c.share_price)}</td>
              </tr>
            )}
            footer={<tr className="border-t-2 border-yellow-600/30 bg-gray-900/40">
              <td className="py-3 text-yellow-400 font-semibold text-sm">TOTAL</td>
              <td className="text-right text-yellow-400 font-bold">{fmt(conversionShareTotal)}</td>
              <td className="text-right text-yellow-400 font-bold">${fmt(conversionUsdtTotal)}</td>
              <td></td>
            </tr>}
          />
        )}
        <ConsolidateButton consolidationType="conversions" userId={userId as number} onSuccess={fetchData} recordCount={data.conversions.length} minRecords={1} totalShares={conversionShareTotal} />
      </AccordionSection>}

      {/* ── Commission Top-Ups Section (read-only funding audit trail) ── */}
      {topups.length > 0 && <AccordionSection title="💳 Commission Top-Ups" count={topups.length} total={`$${fmt(topupTotal)} USDT`} isOpen={!!openSections.topups} onToggle={() => toggle('topups')}>
        <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
              <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Amount</th><th className="text-left pl-4 font-medium">Network</th><th className="text-left pl-4 font-medium">Reference</th><th className="text-right font-medium">Status</th>
            </tr></thead>
            <tbody>
              {topups.map(t => (
                <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
                  <td className="py-2.5 text-gray-300 text-sm">{fmtDate(t.created_at)}</td>
                  <td className="text-right text-green-400 font-medium">${fmt(topupAmount(t))}</td>
                  <td className="pl-4 text-gray-500 text-sm">{topupNetwork(t) || '—'}</td>
                  <td className="pl-4 text-gray-500 text-xs font-mono">{t.transaction_reference || '—'}</td>
                  <td className="text-right"><span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-green-900/40 text-green-300 border border-green-700/40">Credited</span></td>
                </tr>
              ))}
              <tr className="border-t-2 border-yellow-600/30 bg-gray-900/40">
                <td className="py-3 text-yellow-400 font-semibold text-sm">TOTAL</td>
                <td className="text-right text-yellow-400 font-bold">${fmt(topupTotal)}</td>
                <td></td><td></td><td></td>
              </tr>
            </tbody>
          </table></div>
        <p className="mt-3 text-gray-500 text-xs leading-relaxed">
          These USDT top-ups were received and credited to your commission balance. They add to your available balance rather than appearing as separate shares.
        </p>
      </AccordionSection>}

      {/* Consolidate Bonus sits ABOVE the gift table so leaders with dozens of
          BOGO/birthday rows can reach the button without scrolling past the list.
          Modal must match server filter (all active bonus_shares > 0). */}
      {consolidatableBonus.length > 0 && (
        <ConsolidateButton
          consolidationType="bonus_shares"
          userId={userId as number}
          onSuccess={fetchData}
          recordCount={consolidatableBonus.length}
          minRecords={1}
          totalShares={consolidatableBonusTotal}
          breakdownItems={consolidatableBonus.map(p => ({
            label: bonusLabel(p),
            shares: parseFloat(String(p.bonus_shares || 0)),
          }))}
        />
      )}

      {/* ── Bonus Shares Section ─────────────────────────────────── */}
      {bonusPurchases.length > 0 && (
        <AccordionSection title="🎁 Bonus Shares" count={bonusPurchases.length} total={`${fmt(bonusSharesTotal)} bonus shares`} isOpen={!!openSections.bonus} onToggle={() => toggle('bonus')}>
          <p className="text-gray-400 text-xs mb-3 leading-relaxed">
            Free / gift shares from promos (including BOGO). Combining them does not remove them — the list under a combined row is still yours and still counts in the total above.
          </p>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
              <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Bonus Shares</th><th className="text-right font-medium">Purchase</th><th className="text-left pl-4 font-medium">Package</th>
            </tr></thead>
            <tbody>
              {bonusPurchases.map(p => (
                <tr key={p.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
                  <td className="py-2.5 text-gray-300 text-sm">{fmtDate(p.created_at)}</td>
                  <td className="text-right text-yellow-400 font-medium">+{fmt(effectiveRowShares(p))}</td>
                  <td className="text-right text-white">${fmt(p.total_amount)}</td>
                  <td className="pl-4 text-gray-500 text-sm">
                    {bonusLabel(p)}
                    {Array.isArray(p.bonus_breakdown) && p.bonus_breakdown.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-xs text-gray-400">
                        {p.bonus_breakdown.map((row, idx) => (
                          <li key={p.id + '-src-' + idx}>
                            {row.label}: <span className="text-yellow-400">+{fmt(row.shares)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-yellow-600/30 bg-gray-900/40">
                <td className="py-3 text-yellow-400 font-semibold text-sm">TOTAL</td>
                <td className="text-right text-yellow-400 font-bold">+{fmt(bonusSharesTotal)}</td>
                <td></td><td></td>
              </tr>
            </tbody>
          </table></div>
        </AccordionSection>
      )}

      {/* ── Legacy Bonus Section ─────────────────────────────────── */}
      {s.legacyShares > 0 && (
        <AccordionSection title="🏛️ Legacy Bonus" count={1} total={`${fmt(s.legacyShares)} legacy shares`} isOpen={!!openSections.legacy} onToggle={() => toggle('legacy')}>
          <div className="bg-gray-900/40 rounded-lg p-4 border border-gray-700/30">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Legacy bonus shares from previous system</span>
              <span className="text-yellow-400 font-bold text-lg">{fmt(s.legacyShares)} shares</span>
            </div>
          </div>
          <ConsolidateButton consolidationType="legacy_bonus" userId={userId as number} onSuccess={fetchData} recordCount={1} minRecords={1} label="Consolidate Legacy Bonus" totalShares={s.legacyShares} />
        </AccordionSection>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.sentTransfers.length > 0 && <AccordionSection title="📤 Shares Sent" count={data.sentTransfers.length} total={`-${fmt(sentTotal)} shares`} isOpen={!!openSections.sent} onToggle={() => toggle('sent')}>
          {data.sentTransfers.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
                <th className="py-3 text-left font-medium">Date</th><th className="text-right font-medium">Shares</th><th className="text-right font-medium">To</th>
              </tr></thead>
              <tbody>
                {data.sentTransfers.map(t => (
                  <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors">
                    <td className="py-2.5 text-gray-300 text-sm">{fmtDate(t.created_at)}</td>
                    <td className="text-right text-red-400 font-medium">-{fmt(t.shares_transferred)}</td>
                    <td className="text-right text-gray-500 text-sm">#{t.recipient_user_id}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-red-600/30 bg-gray-900/40">
                  <td className="py-3 text-red-400 font-semibold text-sm">TOTAL</td>
                  <td className="text-right text-red-400 font-bold">-{fmt(sentTotal)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table></div>
          )}
        </AccordionSection>}

        {data.receivedTransfers.length > 0 && <AccordionSection title="📥 Shares Received" count={data.receivedTransfers.length} total={`+${fmt(receivedTotal)} shares`} isOpen={!!openSections.received} onToggle={() => toggle('received')}>
          {data.receivedTransfers.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-[11px] uppercase tracking-wider border-b border-gray-700">
                <th className="py-3 text-left font-medium">Transfer Date</th>
                <th className="text-right font-medium">Shares</th>
                <th className="text-right font-medium">Amount</th>
                <th className="text-right font-medium">Price</th>
                <th className="text-left pl-4 font-medium">Package / Phase</th>
                <th className="text-left pl-4 font-medium">Original Purchase</th>
                <th className="text-right font-medium">From</th>
              </tr></thead>
              <tbody>
                {data.receivedTransfers.map(t => {
                  const sp = t.source_purchase || null
                  const fromLabel = t.sender_username ? `@${t.sender_username}` : `#${t.sender_user_id}`
                  const phaseLabel = sp?.phase_id != null ? `Phase ${sp.phase_id}` : null
                  const pkgLabel = displayPackageLabel(sp?.package_name || sp?.payment_method || null) || null
                  const pkgPhase = [pkgLabel, phaseLabel].filter(Boolean).join(' · ')
                  const txHash = t.source_transaction_hash
                  const shortHash = txHash ? `${txHash.slice(0, 10)}…${txHash.slice(-6)}` : null
                  return (
                    <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-700/20 transition-colors align-top">
                      <td className="py-2.5 text-gray-300 text-sm">{fmtDate(t.created_at)}</td>
                      <td className="text-right text-green-400 font-medium">+{fmt(t.shares_transferred)}</td>
                      <td className="text-right text-white">{sp ? `$${fmt(sp.total_amount)}` : '—'}</td>
                      <td className="text-right text-gray-300 text-sm">{sp ? `$${fmt(sp.price_per_share)}` : '—'}</td>
                      <td className="pl-4 text-gray-400 text-sm">{pkgPhase || '—'}</td>
                      <td className="pl-4 text-gray-400 text-xs">
                        {sp?.purchase_date ? <div className="text-gray-300">{fmtDate(sp.purchase_date)}</div> : null}
                        {shortHash ? (
                          <div className="font-mono text-[11px] text-gray-500" title={txHash || ''}>{shortHash}</div>
                        ) : null}
                        {!sp && !shortHash ? <span className="text-gray-600">—</span> : null}
                      </td>
                      <td className="text-right text-gray-400 text-sm">{fromLabel}</td>
                    </tr>
                  )
                })}
                <tr className="border-t-2 border-green-600/30 bg-gray-900/40">
                  <td className="py-3 text-green-400 font-semibold text-sm">TOTAL</td>
                  <td className="text-right text-green-400 font-bold">+{fmt(receivedTotal)}</td>
                  <td colSpan={5}></td>
                </tr>
              </tbody>
            </table></div>
          )}
          <ConsolidateButton consolidationType="received_transfers" userId={userId as number} onSuccess={fetchData} recordCount={data.receivedTransfers.length} minRecords={1} totalShares={receivedTotal} />
        </AccordionSection>}
      </div>
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────
const BreakdownPill: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div className="text-center py-2 px-2 rounded-lg bg-gray-800/60">
    <p className={`text-sm font-semibold ${color || 'text-white'}`}>{value}</p>
    <p className="text-[10px] text-gray-500 uppercase mt-0.5 tracking-wider">{label}</p>
  </div>
)

const AccordionSection: React.FC<{ title: string; count: number; total?: string; isOpen: boolean; onToggle: () => void; children: React.ReactNode }> = ({ title, count, total, isOpen, onToggle, children }) => (
  <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-750 transition-colors">
      <div className="flex items-center gap-3">
        <span className="text-white font-semibold">{title}</span>
        <span className="text-gray-500 text-sm bg-gray-700/50 px-2 py-0.5 rounded-full">{count}</span>
      </div>
      <div className="flex items-center gap-3">
        {total && <span className="text-yellow-400/80 text-sm font-medium hidden sm:inline">{total}</span>}
        <span className={`text-white transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </div>
    </button>
    {isOpen && <div className="px-5 pb-4 border-t border-gray-700">{children}</div>}
  </div>
)

const Empty: React.FC = () => (
  <p className="text-gray-500 text-sm py-6 text-center italic">No records found</p>
)

// ── Pagination ──────────────────────────────────────────────────────────
// Uniform, presentational pagination for the long share/commission lists.
// The parent still computes every TOTAL over the FULL array, so the grand
// total shown in the footer is always the complete list total regardless of
// which page is visible. Only the RENDERED rows are sliced.
const PAGE_SIZE_OPTIONS = [15, 20, 30, 100, 500] as const
const DEFAULT_PAGE_SIZE = 15

// Windowed page list, e.g. [1, '…', 4, 5, 6, 7, 8]. Always shows first + last
// page, the current page, and one neighbour each side; gaps become '…'.
function buildPageList(current: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
  const pages = new Set<number>([1, totalPages, current, current - 1, current + 1])
  const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b)
  const out: (number | '…')[] = []
  let prev = 0
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push('…')
    out.push(p)
    prev = p
  }
  return out
}

const PagerButton: React.FC<{ active?: boolean; disabled?: boolean; onClick?: () => void; children: React.ReactNode; label?: string }> = ({ active, disabled, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-current={active ? 'page' : undefined}
    className={`min-w-[2rem] px-2 py-1 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'bg-yellow-600 text-black'
        : disabled
          ? 'text-gray-600 cursor-not-allowed'
          : 'text-gray-300 bg-gray-800 hover:bg-gray-700'
    }`}
  >
    {children}
  </button>
)

// Generic paginated table. `head` is the <thead> content, `renderRow` renders
// one <tr> per item for the CURRENT page only, and `footer` is the TOTAL <tr>
// (computed by the parent over the full list) rendered on every page.
function PaginatedTable<T>({ items, head, renderRow, footer, initialPageSize = DEFAULT_PAGE_SIZE }: {
  items: T[]
  head: React.ReactNode
  renderRow: (item: T) => React.ReactNode
  footer: React.ReactNode
  initialPageSize?: number
}) {
  const [pageSize, setPageSize] = useState<number>(initialPageSize)
  const [page, setPage] = useState(1)
  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Clamp the current page whenever the data or page size changes.
  const safePage = Math.min(page, totalPages)
  useEffect(() => { if (page > totalPages) setPage(totalPages) }, [page, totalPages])

  const start = (safePage - 1) * pageSize
  const visible = items.slice(start, start + pageSize)
  const showControls = total > PAGE_SIZE_OPTIONS[0]

  return (
    <div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead>{head}</thead>
        <tbody>
          {visible.map(item => renderRow(item))}
          {footer}
        </tbody>
      </table></div>
      {showControls && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-1 border-t border-gray-700/50">
          <label className="flex items-center gap-2 text-gray-400 text-xs">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1) }}
              className="bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-gray-200 text-sm focus:outline-none focus:border-yellow-600"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="hidden sm:inline text-gray-600">
              {start + 1}–{Math.min(start + pageSize, total)} of {total}
            </span>
          </label>
          <div className="flex items-center gap-1 flex-wrap justify-center">
            <PagerButton disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} label="Previous page">‹</PagerButton>
            {buildPageList(safePage, totalPages).map((p, i) =>
              p === '…'
                ? <span key={`e${i}`} className="px-1 text-gray-600 select-none">…</span>
                : <PagerButton key={p} active={p === safePage} onClick={() => setPage(p)} label={`Page ${p}`}>{p}</PagerButton>
            )}
            <PagerButton disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} label="Next page">›</PagerButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default MySharesPage

